import React, { useContext, useEffect, useState } from 'react';
import { Box, Container, Typography, TextField, MenuItem, Button, Tooltip } from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../Commonmodel/Common.css";
import dayjs from "dayjs";
import { CommonContext } from '../../../../context/CommonContext';
import { apisPath } from '../../../../utils/path';
import { authHttp } from '../../../../services/commonHttpService';
import { downloadBlob } from '../../../../helpers/commonHelper';
import PremedicationSelect from '../../../PremedicationSelect/PremedicationSelect';
import XlsIcon from "../../../Images/xls-icon.svg";

const MedicineList = () => {
    const { setIsLoading, setSnackState, getMasterData } = useContext(CommonContext);

    // Filter states
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [drugName, setDrugName] = useState("");
    const [stateLgd, setStateLgd] = useState("");
    const [facilityType, setFacilityType] = useState("");

    // Master data
    const [states, setStates] = useState([]);

    // Table states
    const [tableData, setTableData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // Fetch states on mount
    useEffect(() => {
        const loadStates = async () => {
            const res = await getMasterData(apisPath?.masters?.stateMaster, "states");
            if (res) {
                setStates(res);
            }
        };
        loadStates();
        fetchTableData(); // initial fetch
    }, []);

    const fetchTableData = async (isDownload = false) => {
        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams();
            if (stateLgd) queryParams.append('state_lgd', stateLgd);
            else queryParams.append('state_lgd', '0');

            if (facilityType) queryParams.append('facility_type', facilityType);
            else queryParams.append('facility_type', '0');

            // Default facility_id = 0 as per user example
            queryParams.append('facility_id', '0');

            if (drugName) queryParams.append('drug_name', drugName);
            if (fromDate) queryParams.append('from', fromDate);
            if (toDate) queryParams.append('to', toDate);

            if (isDownload) {
                const response = await authHttp.get(`${apisPath?.reports?.getMedicineReport}?${queryParams.toString()}`, { responseType: 'blob' });
                if (response?.data) {
                    downloadBlob(response.data, `Medicine_Report_${dayjs().format('YYYYMMDD')}.xlsx`);
                    setSnackState({ open: true, message: "Report downloaded successfully!", severity: "success" });
                }
            } else {
                const response = await authHttp.get(`${apisPath?.adminMedicineList}?${queryParams.toString()}`);
                if (response?.data?.status) {
                    setTableData(response.data.data || []);
                    setCurrentPage(1); // reset to first page on new search
                } else {
                    setTableData([]);
                }
            }
        } catch (error) {
            console.error("Error fetching medicine report:", error);
            setSnackState({
                open: true,
                message: error?.response?.data?.message || "Failed to fetch data",
                severity: "error",
            });
            if (!isDownload) setTableData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        fetchTableData();
    };

    const handleDownload = () => {
        fetchTableData(true);
    };

    // Calculate pagination logic
    const totalPages = Math.max(1, Math.ceil(tableData.length / itemsPerPage));
    const currentData = tableData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    backgroundColor: "#FFFFFF",
                    borderBottom: "1px solid #E6E6E6",
                    py: 2,
                }}
            >
                <Container>
                    <Typography variant="h6" fontWeight="600">
                        Day Care Cancer Center
                    </Typography>
                    <Typography variant="body2" color="gray" sx={{ mt: 0.3 }}>
                        State Cancer Care Program - Comprehensive Overview
                    </Typography>
                </Container>
            </Box>

            <Container className="my-4">
                {/* Filter Section */}
                <Box className="bg-white shadow-sm p-4 mb-4" sx={{ borderRadius: "12px", border: "1px solid #E6E6E6" }}>
                    <div className="row g-3 align-items-center">
                        <div className="col-md-2">
                            <TextField
                                label="From"
                                type="date"
                                size="small"
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <TextField
                                label="To"
                                type="date"
                                size="small"
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <PremedicationSelect
                                name="drugName"
                                value={drugName}
                                onChange={(val) => setDrugName(val)}
                                label="Drug Type"
                                size="small"
                                variant="outlined"
                            />
                        </div>
                        <div className="col-md-2">
                            <TextField
                                select
                                label="State"
                                size="small"
                                fullWidth
                                variant="outlined"
                                value={stateLgd}
                                onChange={(e) => setStateLgd(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value="">Select</MenuItem>
                                {states.map((s) => (
                                    <MenuItem key={s?.state_lgd} value={s?.state_lgd}>
                                        {s?.state_name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                        <div className="col-md-2">
                            <TextField
                                select
                                label="Facility Type"
                                size="small"
                                fullWidth
                                variant="outlined"
                                value={facilityType}
                                onChange={(e) => setFacilityType(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="4">TCCC</MenuItem>
                                <MenuItem value="5">DCCC</MenuItem>
                            </TextField>
                        </div>
                        <div className="col-md-1 d-flex justify-content-end">
                            <Button
                                variant="contained"
                                className='dwnBtnAdmin '
                                onClick={handleSearch}
                            // sx={{ minWidth: '40px', height: '40px', padding: 0 }}
                            >
                                <SearchIcon />
                            </Button>
                        </div>
                    </div>
                </Box>

                {/* Table Section */}
                <div className="shadow table-design mb-3 bg-white p-3" style={{ borderRadius: "12px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 tile-header" style={{ fontWeight: 600 }}>Medicine List</h6>
                        <Button
                            variant="contained"
                            className="dwnBtnAdmin"
                            onClick={handleDownload}
                            style={{ gap: "8px" }}
                        >
                            <img src={XlsIcon} alt="Excel" />
                            <span>Download Excel</span>
                        </Button>
                    </div>

                    <div className="table-responsive mt-2">
                        <table className="table align-middle mb-0 custom-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Drug Type</th>
                                    <th>Drug Name</th>
                                    <th>Drug Count</th>
                                    <th>Facility Type</th>
                                    <th>Facilities Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentData.length > 0 ? (
                                    currentData.map((row, i) => (
                                        <tr key={i}>
                                            <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                                            <td>{row?.drug_type || "-"}</td>
                                            <td>{row?.drug_name || "-"}</td>
                                            <td>{row?.drug_count || "0"}</td>
                                            <td>{row?.facility_Type || "-"}</td>
                                            <td>
                                                <Tooltip title={row?.facilities_name || ""} placement="top" arrow>
                                                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
                                                        {row?.facilities_name || "-"}
                                                    </div>
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center p-4">
                                            <Typography variant="body2" color="text.secondary">
                                                No results found
                                            </Typography>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {tableData.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <Typography variant="body2">
                                {tableData.length} results found Showing page {currentPage} of {totalPages}
                            </Typography>
                            <nav>
                                <ul className="pagination mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                    </li>

                                    {(() => {
                                        const pageNumbers = [];

                                        if (totalPages <= 3) {
                                            for (let i = 1; i <= totalPages; i += 1) pageNumbers.push(i);
                                        } else if (currentPage === 1) {
                                            pageNumbers.push(1, 2, 3);
                                        } else if (currentPage === totalPages) {
                                            pageNumbers.push(totalPages - 2, totalPages - 1, totalPages);
                                        } else {
                                            pageNumbers.push(currentPage - 1, currentPage, currentPage + 1);
                                        }

                                        return pageNumbers.map((num) => (
                                            <li key={num} className={`page-item ${currentPage === num ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    style={currentPage === num ? { backgroundColor: '#6c7ff9', borderColor: '#6c7ff9', color: '#fff' } : {}}
                                                    onClick={() => setCurrentPage(num)}
                                                >
                                                    {num}
                                                </button>
                                            </li>
                                        ));
                                    })()}

                                    <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage >= totalPages}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </Container>
        </>
    )
}

export default MedicineList